import {Injectable, inject} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {Observable, switchMap} from 'rxjs';
import {LanguageService} from 'src/app/shared/language.service';
import {TranslationDialogComponent, TranslationMap} from './translation-dialog.component';
import {MasterData} from 'src/app/master-data/master-data.model';
import {TranslationSettingsService} from './translation-settings.service';

export interface TranslationStatusIcon {
  icon: string;
  color: 'primary' | 'accent' | undefined;
  label: string;
}

const TRANSLATION_STATUS_ICONS: Record<'none' | 'partial' | 'complete', TranslationStatusIcon> = {
  none: {
    icon: 'add_comment',
    color: undefined,
    label: $localize`Add translations`,
  },
  partial: {
    icon: 'translate',
    color: 'accent',
    label: $localize`Translations incomplete - some languages are missing`,
  },
  complete: {
    icon: 'task_alt',
    color: 'primary',
    label: $localize`All translations complete`,
  },
};

@Injectable({providedIn: 'root'})
export class TranslationDialogService {
  private readonly dialog = inject(MatDialog);
  private readonly languageService = inject(LanguageService);
  private readonly translationSettingsService = inject(TranslationSettingsService);

  /**
   * Icon/color/aria-label to show for an item's "translations" column,
   * reflecting whether none, some, or all of the currently selected tenant
   * languages have been filled in.
   */
  getStatusIcon(translations?: TranslationMap): TranslationStatusIcon {
    const status = this.translationSettingsService.getTranslationStatus(translations);
    return TRANSLATION_STATUS_ICONS[status];
  }

  open(item: MasterData, entityLabel: string): Observable<TranslationMap | undefined> {
    return this.languageService.loadLanguages().pipe(
      switchMap(() => {
        const selectedLanguages = this.translationSettingsService.selectedLanguages();

        const dialogRef = this.dialog.open(TranslationDialogComponent, {
          width: '600px',
          data: {
            entityLabel,
            languages: selectedLanguages,
            translation: item.translations ?? {}
          }
        });

        return dialogRef.afterClosed();
      })
    );
  }
}
